import { useContext, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { GeneralQueryKeys, Navigation } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"
import Loading from "../../../components/common/Loading"
import { ModalContext } from "../../../components/contexts/ModalContext"
import BaseButton from "../../../components/elements/BaseButton"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import ErrorPage from "../../404/ErrorPage"
import { useDelayWhenSearchWithPagination } from "../../../services/functions"
import { getEmployeesForTourAssignment, getTour, getTourList } from "../api/apiCalls"
import {
    useAddTourStopMutation,
    useMoveTourStopToTourMutation,
    useMoveTourStopWeekdayMutation,
    useRemoveTourStopMutation,
    useReorderTourStopMutation,
    useUpdateTourMutation,
} from "../api/services"
import { ReactComponent as BACKARROWLEFT } from "../../../assets/BackArrowLeft.svg"
import { ReactComponent as EDIT } from "../../../assets/EditICON.svg"
import AddStopModal from "../components/AddStopModal"
import EditTourModal from "../components/EditTourModal"
import TourWeekBoard from "../components/TourWeekBoard"
import { TourWeekday } from "../types"

export default function TourDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const ability = useAbility()
    const canEdit = ability.can(P.termine_management.action, P.termine_management.subject)
    const modalCon = useContext(ModalContext)
    const queryClient = useQueryClient()
    const [addWeekday, setAddWeekday] = useState<TourWeekday>(1)
    const [searchEmployee, setSearchEmployee] = useState("")
    const searchEmployeeParams = useDelayWhenSearchWithPagination(searchEmployee)

    const { data: tour, isLoading, error } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_SINGLE + (id ?? "")],
        queryFn: () => getTour(id ?? ""),
        enabled: !!id,
    })
    const { data: allTours } = useQuery({
        queryKey: [GeneralQueryKeys.TOURS],
        queryFn: getTourList,
    })
    const { data: employees, isLoading: employeesLoading } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + "tour" + searchEmployeeParams],
        queryFn: () => getEmployeesForTourAssignment(searchEmployeeParams),
    })
    const { mutate: updateTour } = useUpdateTourMutation(queryClient)
    const { mutate: addStop } = useAddTourStopMutation(queryClient)
    const { mutate: removeStop } = useRemoveTourStopMutation(queryClient)
    const { mutate: reorderStop } = useReorderTourStopMutation(queryClient)
    const { mutate: moveWeekday } = useMoveTourStopWeekdayMutation(queryClient)
    const { mutate: moveToTour } = useMoveTourStopToTourMutation(queryClient)

    const otherTours = useMemo(() => (allTours ?? []).filter((item) => item._id !== id), [allTours, id])
    const usedObjectIds = useMemo(() => (tour?.stops.filter((stop) => !stop.isAdHoc).map((stop) => stop.object._id) ?? []), [tour])

    if (isLoading) return <Loading />
    if (error || !tour) return <ErrorPage />

    return (
        <BasePage>
            <BaseHeadBanner title={tour.name} />
            <BaseButtonRow>
                <BaseButton
                    label="Zur Übersicht"
                    icon={<BACKARROWLEFT className="w-5 h-5 shrink-0" />}
                    func={() => navigate(Navigation.TOURS)}
                    variant="ghost"
                    classProps="h-12"
                />
                {canEdit ? (
                    <BaseButton label="Bearbeiten" icon={<EDIT className="w-5 h-5" />} func={() => modalCon?.trigger(3)} colorway="secondary" classProps="h-12"/>
                ) : (
                    <span />
                )}
            </BaseButtonRow>
            <BaseGrowBody classProps="overflow-hidden my-2 grow min-h-0">
                <TourWeekBoard
                    tour={tour}
                    otherTours={otherTours}
                    canEdit={canEdit}
                    onAddStop={(weekday) => {
                        setAddWeekday(weekday)
                        modalCon?.trigger(2)
                    }}
                    onRemoveStop={(stopId) => removeStop({ tourId: tour._id, stopId })}
                    onMoveUp={(stopId) => reorderStop({ tourId: tour._id, stopId, direction: -1 })}
                    onMoveDown={(stopId) => reorderStop({ tourId: tour._id, stopId, direction: 1 })}
                    onChangeWeekday={(stopId, weekday) => moveWeekday({ tourId: tour._id, stopId, weekday })}
                    onMoveToTour={(stopId, toTourId, weekday) => moveToTour({ fromTourId: tour._id, toTourId, stopId, weekday })}
                />
            </BaseGrowBody>
            <EditTourModal
                modalId={3}
                tour={tour}
                employees={employees ?? []}
                employeesLoading={employeesLoading}
                onSearchEmployee={setSearchEmployee}
                onSave={({ name, employee, active }) => {
                    updateTour({ id: tour._id, name, active, employee: employee?._id ?? null })
                }}
            />
            <AddStopModal
                modalId={2}
                defaultWeekday={addWeekday}
                usedObjectIds={usedObjectIds}
                onSave={({ object, weekday, adHoc }) => {
                    addStop({
                        tourId: tour._id,
                        object,
                        weekday,
                        extra: adHoc ? { isAdHoc: true, adHocTask: adHoc, previewStatus: "open" } : undefined,
                    })
                }}
            />
        </BasePage>
    )
}
